import React, { useState } from "react";
import { Building2, Crown, Repeat, ShieldCheck, Clock, FileCheck, Check, ArrowRight } from "lucide-react";

const SEGMENTS = [
  {
    id: "reguliers",
    label: "Réguliers",
    icon: Repeat,
    client: {
      initials: "MP",
      color: "bg-violet-600",
      name: "Marc Petit",
      badge: "Client régulier",
      badgeClass: "bg-violet-100 text-violet-700",
      stats: [["Locations", "6"], ["CA généré", "2 140 €"], ["Client depuis", "2025"]],
      note: "Réserve une fois par mois. Toujours à l'heure.",
    },
    payments: [
      { icon: ShieldCheck, label: "Caution 500 €", sub: "Empreinte bancaire · non débitée", status: "Sécurisée" },
      { icon: Check, label: "Location 180 €", sub: "Encaissée automatiquement", status: "Payée" },
      { icon: Clock, label: "Virement 180 €", sub: "Sur votre compte sous 48 h", status: "Programmé" },
      { icon: FileCheck, label: "Facture #F-2841", sub: "Envoyée au client, rattachée au dossier", status: "Envoyée" },
    ],
  },
  {
    id: "vip",
    label: "VIP",
    icon: Crown,
    client: {
      initials: "SL",
      color: "bg-fuchsia-600",
      name: "Sophie Laurent",
      badge: "★ VIP",
      badgeClass: "bg-amber-100 text-amber-700",
      stats: [["Locations", "12"], ["CA généré", "8 400 €"], ["Cliente depuis", "2024"]],
      note: "Votre meilleure cliente. Priorité sur les nouveaux véhicules.",
    },
    payments: [
      { icon: ShieldCheck, label: "Caution 800 €", sub: "Empreinte bancaire · non débitée", status: "Sécurisée" },
      { icon: Check, label: "Location 340 €", sub: "Encaissée automatiquement", status: "Payée" },
      { icon: Clock, label: "Virement 340 €", sub: "Sur votre compte sous 48 h", status: "Programmé" },
      { icon: FileCheck, label: "Facture #F-2856", sub: "Envoyée à la cliente, rattachée au dossier", status: "Envoyée" },
    ],
  },
  {
    id: "entreprises",
    label: "Entreprises",
    icon: Building2,
    client: {
      initials: "TE",
      color: "bg-indigo-600",
      name: "Transports Éclair",
      badge: "Contrat cadre",
      badgeClass: "bg-indigo-100 text-indigo-700",
      stats: [["Locations", "28"], ["CA généré", "19 600 €"], ["Client depuis", "2024"]],
      note: "Facturation mensuelle groupée, avec TVA.",
    },
    payments: [
      { icon: ShieldCheck, label: "Caution 1 200 €", sub: "Empreinte bancaire · non débitée", status: "Sécurisée" },
      { icon: Check, label: "Forfait mensuel 1 450 €", sub: "Encaissé automatiquement", status: "Payé" },
      { icon: Clock, label: "Virement 1 450 €", sub: "Sur votre compte sous 48 h", status: "Programmé" },
      { icon: FileCheck, label: "Facture TVA #F-2860", sub: "Groupée, envoyée au service compta", status: "Envoyée" },
    ],
  },
];

const STATUS_CLASS = {
  "Sécurisée": "bg-emerald-50 text-emerald-700",
  "Payée": "bg-emerald-50 text-emerald-700",
  "Payé": "bg-emerald-50 text-emerald-700",
  "Programmé": "bg-violet-50 text-violet-700",
  "Envoyée": "bg-slate-100 text-slate-600",
};

function Beam() {
  return (
    <div aria-hidden className="flex items-center justify-center lg:h-full">
      {/* Horizontal (desktop) */}
      <div className="hidden lg:block relative w-20 h-[2px]">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            backgroundImage: "linear-gradient(90deg, #7C3AED 0 10px, transparent 10px 16px)",
            backgroundSize: "16px 2px",
            animation: "jnFlow 0.9s linear infinite",
          }}
        />
        <span className="absolute -top-[3px] h-2 w-2 rounded-full bg-fuchsia-500 shadow-[0_0_10px_rgba(217,70,239,0.8)]" style={{ animation: "jnBeamDot 2.2s ease-in-out infinite" }} />
      </div>
      {/* Vertical (mobile) */}
      <div className="lg:hidden relative h-14 w-[2px] my-2">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            backgroundImage: "linear-gradient(180deg, #7C3AED 0 10px, transparent 10px 16px)",
            backgroundSize: "2px 16px",
            animation: "jnFlowY 0.9s linear infinite",
          }}
        />
      </div>
    </div>
  );
}

export default function ClientPaymentBlock() {
  const [seg, setSeg] = useState(0);
  const data = SEGMENTS[seg];
  const c = data.client;

  return (
    <div data-testid="client-payment-block">
      <div className="max-w-2xl mb-10">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-violet-600 mb-4">CRM & Paiements</div>
        <h3 className="font-heading text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight leading-tight text-slate-900">
          Du client à l&apos;encaissement.
          <br />
          <span className="text-violet-600">Chaque euro est rattaché à un visage.</span>
        </h3>
      </div>

      {/* Segments */}
      <div className="flex flex-wrap gap-2 mb-8">
        {SEGMENTS.map((s, i) => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              onClick={() => setSeg(i)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold transition-colors duration-300 ${
                i === seg ? "bg-violet-600 text-white shadow-md shadow-violet-600/25" : "bg-white border border-slate-200 text-slate-600 hover:border-violet-300"
              }`}
              data-testid={`seg-tab-${s.id}`}
            >
              <Icon className="h-3.5 w-3.5" /> {s.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 md:p-10 grid lg:grid-cols-[1fr_auto_1.1fr] gap-4 lg:gap-6 items-stretch">
        {/* Fiche client */}
        <div key={`client-${data.id}`} style={{ animation: "jnMaterialize 0.5s cubic-bezier(0.22,1,0.36,1) both" }} data-testid="client-card">
          <div className="h-full rounded-2xl bg-slate-50 border border-slate-100 p-6">
            <div className="flex items-center gap-4">
              <span className={`h-14 w-14 rounded-2xl ${c.color} flex items-center justify-center text-base font-bold text-white`}>{c.initials}</span>
              <div>
                <div className="font-heading text-lg font-semibold text-slate-900">{c.name}</div>
                <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${c.badgeClass}`}>{c.badge}</span>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-2">
              {c.stats.map(([l, v]) => (
                <div key={l} className="rounded-xl bg-white border border-slate-100 py-3 text-center">
                  <div className="text-[15px] font-bold text-slate-900">{v}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{l}</div>
                </div>
              ))}
            </div>
            <p className="mt-5 text-[12px] text-slate-500 leading-relaxed italic">« {c.note} »</p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-violet-600">
              Voir l&apos;historique complet <ArrowRight className="h-3 w-3" />
            </div>
          </div>
        </div>

        <Beam />

        {/* Timeline paiements */}
        <div key={`pay-${data.id}`} style={{ animation: "jnMaterialize 0.5s cubic-bezier(0.22,1,0.36,1) both", animationDelay: "120ms" }} data-testid="payments-timeline">
          <div className="h-full rounded-2xl border border-slate-100 p-6">
            <div className="text-[11px] font-bold tracking-[0.14em] uppercase text-slate-400 mb-4">Flux de paiement · ce client</div>
            <div className="space-y-1">
              {data.payments.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={p.label} className="relative flex items-center gap-3.5 py-2.5">
                    {i < data.payments.length - 1 && <span aria-hidden className="absolute left-[17px] top-11 h-4 w-[2px] bg-slate-100" />}
                    <span className="h-9 w-9 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-violet-600" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-slate-900">{p.label}</div>
                      <div className="text-[11px] text-slate-500">{p.sub}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${STATUS_CLASS[p.status]}`}>{p.status}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Puces clés */}
      <div className="mt-6 flex flex-wrap gap-2.5">
        {["Empreinte bancaire sans débit", "Virements automatiques sous 48 h", "Facturation auto rattachée au client"].map((t) => (
          <span key={t} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-[12px] font-medium text-slate-600">
            <Check className="h-3 w-3 text-violet-600" strokeWidth={3} /> {t}
          </span>
        ))}
      </div>
    </div>
  );
}
